import { FC } from "react";
import { useTranslation } from "react-i18next";
import {AirplaneIcon} from "src/assets/Icons";
import { Timeline } from "src/components";
import { JourneyType, SegmentType } from "src/Modules/Flights/constants";
import { explode, formatTimeFromUnit } from "src/utility";
import { formatDate } from "src/utility/time";
import FareRulesDrawer from "../FareRulesDrawer/FareRulesDrawer";
import { Divider } from "antd";

const SegmentSummaryCard: FC<{ segment: SegmentType; index: number }> = ({ segment, index }) => {
  const { t } = useTranslation();
  const { arrival, departure, destination, stops, origin, durationMinutes } = explode(segment, {});
  const stopLabel =
    stops === 0 ? t("Flights.listing.direct") : stops === 1 ? t("Flights.listing.oneStop") : t("Flights.listing.multipleStops", { count: stops });
  const title = index === 0 ? t("Flights.search.departure") : t("Flights.search.return");

  const parsedArrivalTime = formatDate(arrival, "hh:mm A");
  const parsedDepartureTime = formatDate(departure, "hh:mm A");
  const duration = formatTimeFromUnit(durationMinutes, "m");
  const OriginItem = (
    <div className="flex flex-1 flex-col items-start justify-center">
      <div className="text-lg font-semibold">{origin}</div>
      <div className="text-font-subtle justity-center flex items-center gap-1 whitespace-nowrap">{parsedDepartureTime} </div>
    </div>
  );
  const DestinationItem = (
    <div className="flex flex-1 flex-col items-end justify-center">
      <div className="text-lg font-semibold">{destination}</div>
      <div className="text-font-subtle justity-start flex items-center gap-1 whitespace-nowrap">{parsedArrivalTime}</div>
    </div>
  );
  const ConnectorOverlay = (
    <div className="text-font-secondary flex flex-col items-center justify-center gap-1 whitespace-nowrap rounded-full bg-background-default text-sm font-semibold">
      <div>{duration}</div>
      <div>{<AirplaneIcon className="h-5 w-5" transform="rotate(-90)" fill="var(--color-font-secondary)"/>}</div>
      <div>{stopLabel}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3 p-5">
      <div className="text-font-default text-base font-semibold">{title}</div>
      <div className="flex w-full items-center justify-between">
        {OriginItem}
        <div className="flex-4">
          <Timeline
            items={[<></>, <></>]}
            connectorOverlays={[ConnectorOverlay]}
            classNames={{
              connector: {
                rail: "flex-5 text-background-subtle border-border-subtle h-0! border-dashed! border-t! translate-y-1/2!",
                head: "bg-background-subtle border-2",
              },
            }}
          />
        </div>

        {DestinationItem}
      </div>
    </div>
  );
};

const JourneySummaryCard: FC<{ journey: JourneyType }> = ({ journey }) => {
  return (
    <div
      className="rounded-xs bg-background-default flex flex-col shadow-sm"
    >
      {journey.segments.map((segment, index) => {
        return <SegmentSummaryCard segment={segment} index={index} key={`segment-${index}`} />;
      })}
      <Divider className="m-0! w-3/5!" dashed/>
      <FareRulesDrawer/>
    </div>
  );
};

export default JourneySummaryCard;
